import { Avatar, Card, Descriptions, Flex, Space, Tag, Typography } from "antd";
import { actions as routerActions } from "redux-router5";
import { avatarColor } from "../../../common/functions.ts";
import { routeNames } from "../../common/routes.ts";
import type { TicketViewRenderProps } from "./TicketViewRender.tsx";
import { dateValue } from "../../../common/columns.tsx";
import { NoData } from "../../../common/components/NoDataComp.tsx";
import { formatTotalTime } from "../../../common/utils/formatTotalTime.ts";
import { formatHours } from "../../../common/utils/formatHours.ts";
import { sumEstimatedTime, sumTaskLoggedTime } from "../../../common/utils/taskTimeUtils.ts";
import {
  delayReasonLabelMap,
  resolutionColorMap,
  resolutionLabelMap,
  statusColorMap,
  statusLabelMap,
} from "../ticketConstants.ts";

export const TicketInfo = (props: TicketViewRenderProps) => {
  const { intl, ticketData, dispatch } = props;
  const { ticket } = ticketData;

  const estimatedSum = sumEstimatedTime(ticket.items ?? []);
  const loggedSum = sumTaskLoggedTime(ticket.items ?? []);

  const statusEl =
    ticket.status && ticket.status in statusLabelMap ? (
      <Tag
        color={statusColorMap[ticket.status]}
        variant="outlined"
        style={{ fontWeight: 700 }}
      >
        {statusLabelMap[ticket.status]}
      </Tag>
    ) : (
      <NoData intl={intl} />
    );

  const resolutionEl =
    ticket.resolution && ticket.resolution in resolutionLabelMap ? (
      <Tag
        color={resolutionColorMap[ticket.resolution]}
        variant="outlined"
        style={{ fontWeight: 700 }}
      >
        {resolutionLabelMap[ticket.resolution]}
      </Tag>
    ) : null;

  const delayReasonEl =
    ticket.delay_reason && ticket.delay_reason in delayReasonLabelMap ? (
      <Typography.Text>{delayReasonLabelMap[ticket.delay_reason]}</Typography.Text>
    ) : null;

  return (
    <Card title={intl.formatMessage({ id: "ticket_section_details" })}>
      <Descriptions bordered column={2}>
        <Descriptions.Item
          label={intl.formatMessage({ id: "table_column_service" })}
          span={2}
        >
          {ticket.service?.name ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item
          label={intl.formatMessage({ id: "ticket_description" })}
          span={2}
        >
          {ticket.description}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "table_column_status" })}>
          {statusEl}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "table_column_client" })}>
          {dispatch ? (
            <Typography.Link
              onClick={() =>
                dispatch(
                  routerActions.navigateTo(routeNames.CLIENT_VIEW, {
                    id: ticket.client.id,
                  })
                )
              }
            >
              {ticket.client.legal_name}
            </Typography.Link>
          ) : (
            ticket.client.legal_name
          )}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "project_label" })}>
          {ticket.project ? (
            dispatch ? (
              <Typography.Link
                onClick={() =>
                  dispatch(
                    routerActions.navigateTo(routeNames.PROJECT_VIEW, {
                      id: ticket.project!.id,
                    })
                  )
                }
              >
                {ticket.project.name ?? ticket.project.id}
              </Typography.Link>
            ) : (
              ticket.project.name ?? ticket.project.id
            )
          ) : (
            <NoData intl={intl} />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "table_column_turbine" })}>
          {dispatch ? (
            <Typography.Link
              onClick={() =>
                dispatch(
                  routerActions.navigateTo(routeNames.TURBINE_VIEW, {
                    id: ticket.turbine.id,
                  })
                )
              }
            >
              {ticket.turbine.serial_number}
            </Typography.Link>
          ) : (
            ticket.turbine.serial_number
          )}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "ticket_location" })}>
          {dispatch ? (
            <Typography.Link
              onClick={() =>
                dispatch(
                  routerActions.navigateTo(routeNames.LOCATION_VIEW, {
                    id: ticket.location.id,
                  })
                )
              }
            >
              {ticket.location.name}
            </Typography.Link>
          ) : (
            ticket.location.name
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={intl.formatMessage({ id: "ticket_client_case_number" })}
        >
          {ticket.client_case_nr ?? <NoData intl={intl} />}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "ticket_coordinator" })}>
          {ticket.coordinators.length > 0 ? (
            <Flex vertical gap="small">
              {ticket.coordinators.map((c) => (
                <Space key={c.id} align="center" size="small">
                  <Avatar
                    size="small"
                    style={{ backgroundColor: avatarColor(`${c.coordinator.first_name ?? c.coordinator.username} ${c.coordinator.last_name ?? ""}`.trim()) }}
                  >
                    {(c.coordinator.first_name ?? c.coordinator.username).charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography.Text>{`${c.coordinator.first_name ?? c.coordinator.username} ${c.coordinator.last_name ?? ""}`.trim()}</Typography.Text>
                </Space>
              ))}
            </Flex>
          ) : (
            <NoData intl={intl} />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "ticket_manager" })}>
          {ticket.managers.length > 0 ? (
            <Flex vertical gap="small">
              {ticket.managers.map((m) => (
                <Space key={m.id} align="center" size="small">
                  <Avatar
                    size="small"
                    style={{ backgroundColor: avatarColor(`${m.manager.first_name ?? m.manager.username} ${m.manager.last_name ?? ""}`.trim()) }}
                  >
                    {(m.manager.first_name ?? m.manager.username).charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography.Text>{`${m.manager.first_name ?? m.manager.username} ${m.manager.last_name ?? ""}`.trim()}</Typography.Text>
                </Space>
              ))}
            </Flex>
          ) : (
            <NoData intl={intl} />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "table_column_created_by" })}>
          <Space align="center" size="small">
            <Avatar
              size="small"
              style={{ backgroundColor: avatarColor(`${ticket.created_by.first_name ?? ticket.created_by.username} ${ticket.created_by.last_name ?? ""}`.trim()) }}
            >
              {(ticket.created_by.first_name ?? ticket.created_by.username).charAt(0).toUpperCase()}
            </Avatar>
            {`${ticket.created_by.first_name ?? ticket.created_by.username} ${ticket.created_by.last_name ?? ""}`.trim()}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item
          label={intl.formatMessage({ id: "table_column_assignee" })}
          span={2}
        >
          {ticket.assignees.length > 0 ? (
            <Flex vertical gap="small">
              {ticket.assignees.map((a) => (
                <Space key={a.id} align="center" size="small">
                  <Avatar
                    size="small"
                    style={{ backgroundColor: avatarColor(`${a.assignee.first_name ?? a.assignee.username} ${a.assignee.last_name ?? ""}`.trim()) }}
                  >
                    {(a.assignee.first_name ?? a.assignee.username).charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography.Text>{`${a.assignee.first_name ?? a.assignee.username} ${a.assignee.last_name ?? ""}`.trim()}</Typography.Text>
                </Space>
              ))}
            </Flex>
          ) : (
            <NoData intl={intl} />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "table_column_start" })}>
          {ticket.start ? dateValue(ticket.start, intl) : <NoData intl={intl} />}
        </Descriptions.Item>
        <Descriptions.Item label={intl.formatMessage({ id: "ticket_deadline" })}>
          {ticket.deadline ? dateValue(ticket.deadline, intl) : <NoData intl={intl} />}
        </Descriptions.Item>
        {(ticket.actual_start ||
          ticket.actual_end ||
          ticket.total_time != null ||
          ticket.total_standby_time != null) && (
          <>
            <Descriptions.Item label={intl.formatMessage({ id: "ticket_actual_start" })}>
              {ticket.actual_start ? (
                dateValue(ticket.actual_start, intl)
              ) : (
                <NoData intl={intl} />
              )}
            </Descriptions.Item>
            <Descriptions.Item label={intl.formatMessage({ id: "ticket_actual_end" })}>
              {ticket.actual_end ? (
                dateValue(ticket.actual_end, intl)
              ) : (
                <NoData intl={intl} />
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage({ id: "ticket_total_time_label" })}
            >
              <Typography.Text>{formatTotalTime(ticket.total_time)}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={intl.formatMessage({ id: "ticket_total_standby_time_label" })}
            >
              <Typography.Text>
                {formatTotalTime(ticket.total_standby_time)}
              </Typography.Text>
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item
          label={intl.formatMessage({ id: "ticket_estimated_task_time_label" })}
        >
          <Typography.Text>{formatHours(estimatedSum)}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item
          label={intl.formatMessage({ id: "ticket_logged_task_time_label" })}
        >
          <Typography.Text>{formatHours(loggedSum)}</Typography.Text>
        </Descriptions.Item>
        {resolutionEl && (
          <Descriptions.Item label={intl.formatMessage({ id: "ticket_resolution" })}>
            {resolutionEl}
          </Descriptions.Item>
        )}
        {delayReasonEl && (
          <Descriptions.Item label={intl.formatMessage({ id: "ticket_delay_reason" })}>
            {delayReasonEl}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};
